import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Badge,
  Avatar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  VideoLibrary as VideoLibraryIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as LogoutIcon,
  Construction as MaintenanceIcon
} from '@mui/icons-material';

// Import admin components
import AdminOverview from '../components/admin/AdminOverview';
import UserManagement from '../components/admin/UserManagement';
import CampaignManagement from '../components/admin/CampaignManagement';
import SubmissionManagement from '../components/admin/SubmissionManagement';
import NotificationCenter from '../components/admin/NotificationCenter';
import PlatformSettings from '../components/admin/PlatformSettings';
import RevenueAnalytics from '../components/admin/RevenueAnalytics';
import AuditLogs from '../components/admin/AuditLogs';

export default function OwnerDashboard() {
  const [currentPage, setCurrentPage] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Platform owner password - in production, this should be more secure
  const OWNER_PASSWORD = 'admin123'; // Change this to a secure password

  useEffect(() => {
    // Check if user is authenticated as owner
    const isOwnerAuth = localStorage.getItem('ownerAuthenticated');
    if (!isOwnerAuth) {
      setPasswordDialog(true);
    } else {
      setAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = () => {
    if (enteredPassword === OWNER_PASSWORD) {
      setAuthenticated(true);
      setPasswordDialog(false);
      localStorage.setItem('ownerAuthenticated', 'true');
    } else {
      alert('Incorrect password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ownerAuthenticated');
    navigate('/');
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: <DashboardIcon /> },
    { id: 'users', label: 'User Management', icon: <PeopleIcon /> },
    { id: 'campaigns', label: 'Campaign Management', icon: <CampaignIcon /> },
    { id: 'submissions', label: 'Submission Management', icon: <VideoLibraryIcon /> },
    { id: 'notifications', label: 'Notification Center', icon: <NotificationsIcon /> },
    { id: 'revenue', label: 'Revenue Analytics', icon: <MoneyIcon /> },
    { id: 'settings', label: 'Platform Settings', icon: <SettingsIcon /> },
    { id: 'audit', label: 'Audit Logs', icon: <SecurityIcon /> }
  ];

  const DrawerContent = () => (
    <Box sx={{ width: 250, pt: 2 }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#ff5252', fontWeight: 'bold' }}>
          Admin Panel
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={maintenanceMode}
              onChange={(e) => setMaintenanceMode(e.target.checked)}
              color="warning"
            />
          }
          label="Maintenance Mode"
          sx={{ mt: 1 }}
        />
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.id}
            onClick={() => {
              setCurrentPage(item.id);
              if (isMobile) setDrawerOpen(false);
            }}
            selected={currentPage === item.id}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 82, 82, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 82, 82, 0.2)',
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: currentPage === item.id ? '#ff5252' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: currentPage === item.id ? '#ff5252' : 'inherit',
                  fontWeight: currentPage === item.id ? 'bold' : 'normal'
                } 
              }} 
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'overview':
        return <AdminOverview />;
      case 'users':
        return <UserManagement />;
      case 'campaigns':
        return <CampaignManagement />;
      case 'submissions':
        return <SubmissionManagement />;
      case 'notifications':
        return <NotificationCenter />;
      case 'revenue':
        return <RevenueAnalytics />;
      case 'settings':
        return <PlatformSettings maintenanceMode={maintenanceMode} setMaintenanceMode={setMaintenanceMode} />;
      case 'audit':
        return <AuditLogs />;
      default:
        return <AdminOverview />;
    }
  };

  if (!authenticated) {
    return (
      <Dialog open={passwordDialog} disableEscapeKeyDown>
        <DialogTitle>Owner Access Required</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please enter the owner password to access the admin dashboard.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Owner Password"
            value={enteredPassword}
            onChange={(e) => setEnteredPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/')}>Cancel</Button>
          <Button 
            onClick={handlePasswordSubmit}
            variant="contained"
            sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
          >
            Access Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {maintenanceMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            p: 1,
            textAlign: 'center',
            zIndex: 9999
          }}
        >
          <MaintenanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          MAINTENANCE MODE ACTIVE - Platform is in maintenance mode
        </Box>
      )}

      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: '#ff5252', 
          zIndex: theme.zIndex.drawer + 1,
          mt: maintenanceMode ? '48px' : 0
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 'bold'
            }}
          >
            Clipping4u - Owner Dashboard
          </Typography>
          <IconButton
            size="large"
            aria-label="account menu"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            color="inherit"
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={isMobile ? drawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: 250,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 250,
            boxSizing: 'border-box',
            top: maintenanceMode ? '112px' : '64px',
            height: maintenanceMode ? 'calc(100% - 112px)' : 'calc(100% - 64px)',
          },
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          mt: maintenanceMode ? '112px' : '64px',
          ml: isMobile ? 0 : '250px',
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {renderCurrentPage()}
      </Box>
    </Box>
  );
}
